import { bool, func, object, shape } from 'prop-types';
import React, { useRef } from 'react';
import _get from 'lodash/get';
import _uniqBy from 'lodash/uniqBy';
import _uniqWith from 'lodash/uniqWith';
import _isEqual from 'lodash/isEqual';
import _sortBy from 'lodash/sortBy';
import styled from 'styled-components';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import templateReplace from 'es6-template-strings';

import ListView from 'ravenjs/components/ListView';
import Typography from 'ravenjs/lib/Typography';
import { callFunc } from 'ravenjs/utils/actions';

import MESSAGES from 'constants/messages';
import { MODALS } from 'constants/modals';
import { actions as uiActions, selectors as uiSelectors } from 'modules/ui';
import { useReactToPrint } from 'react-to-print';

import schema from './DuplicateSkusListSchema.json';
import Modal from '../Modal';

const DuplicateSkuModalStyled = styled.div`
    @media print {
        padding: 1rem;
    }
`;
const DuplicateSkusDescription = styled.div`
    margin-bottom: 1rem;
    margin-top: 2rem;
`;

const DuplicateSkusModal = ({ closeModal, modal, openModal }) => {
    const componentRef = useRef();
    const handlePrint = useReactToPrint({
        content: () => componentRef.current,
    });
    const handleOnContinue = () => {
        const onAddSku = _get(modal, 'data.onAddSku', null);
        const selectedSkus = _get(modal, 'data.selectedSkus', []);
        callFunc(onAddSku, selectedSkus);
        handleOnCancel();
    };
    const getActions = () => {
        return [
            {
                onClick: handleOnCancel,
                text: 'Cancel',
                color: 'cancelButton',
            },
            {
                onClick: handlePrint,
                text: 'Print',
                color: 'primary',
            },
            {
                hide: false,
                onClick: handleOnContinue,
                text: 'Continue',
                color: 'primary',
            },
        ];
    };

    const getModalProps = () => {
        const open = _get(modal, 'open', false);

        return {
            actions: getActions(),
            content: renderContent(),
            id: MODALS.DUPLICATE_SKUS,
            modalBodyProps: {
                padding: '15px',
            },
            open,
        };
    };

    const handleOnCancel = () => {
        closeModal(MODALS.DUPLICATE_SKUS);
    };

    const renderAvailableSkuToAdd = () => {
        let availableSkus = _get(modal, 'data.availableSkus', []);
        availableSkus = _sortBy(availableSkus, ['name']);

        if (!availableSkus.length) {
            return null;
        }

        const availableSkuSchema = {
            ...schema,
            columns: [
                {
                    title: 'Products and Services',
                    id: 'name',
                },
                {
                    title: 'Description',
                    id: 'description',
                },
            ],
        };
        return (
            <>
                <Typography type="subheading">
                    {MESSAGES.MODAL.ADD_SKU.CONFIRM.DESCRIPTION}
                </Typography>
                <ListView data={availableSkus} schema={availableSkuSchema} />
            </>
        );
    };

    const renderDuplicateSkusList = () => {
        let duplicateSkus = _get(modal, 'data.duplicateSkus', []);
        let message = MESSAGES.MODAL.DUPLICATE_SKU.TITLE;
        const DescriptionLabel = DuplicateSkusDescription;
        duplicateSkus = _uniqWith(duplicateSkus, _isEqual);
        duplicateSkus = _sortBy(duplicateSkus, ['companyName']);
        const duplicateSkusString = _uniqBy(duplicateSkus, 'name')
            .map(item => item.name)
            .join(', ');
        message = templateReplace(MESSAGES.MODAL.DUPLICATE_SKU.TITLE_SPONSORSHIP, {
            skusToReplace: duplicateSkusString,
        });

        return (
            <>
                {duplicateSkus.length ? (
                    <DescriptionLabel type="subheading">{message}</DescriptionLabel>
                ) : null}
                {<ListView data={duplicateSkus} schema={schema} />}
            </>
        );
    };

    const renderContent = () => {
        return (
            <DuplicateSkuModalStyled ref={componentRef}>
                {renderAvailableSkuToAdd()}
                {renderDuplicateSkusList()}
            </DuplicateSkuModalStyled>
        );
    };

    return <Modal {...getModalProps()} />;
};

DuplicateSkusModal.propTypes = {
    closeModal: func.isRequired,
    openModal: func.isRequired,
    modal: shape({
        data: object,
        open: bool,
    }),
};

DuplicateSkusModal.defaultProps = {
    modal: {
        open: false,
        data: null,
    },
};

const mapStateToProps = createStructuredSelector({
    modal: uiSelectors.getModalById(MODALS.DUPLICATE_SKUS),
});

const mapDispatchToProps = {
    closeModal: uiActions.closeModal,
    openModal: uiActions.openModal,
};

export { DuplicateSkusModal as DuplicateSkusModalUnwrapped };

export default connect(mapStateToProps, mapDispatchToProps)(DuplicateSkusModal);
